/**
 * korsha-gallery.js — the product media gallery and its full-screen viewer.
 *
 *   <korsha-gallery>   swipe position, counter, dots, thumbnails, variant
 *                      image selection, hover magnifier, deferred video/3D
 *   Lightbox           the full-screen viewer: one image at a time, pinch and
 *                      double-tap zoom with panning on touch, click-to-zoom
 *                      that follows the pointer on desktop, swipe or arrow
 *                      keys between images, swipe down to close
 *
 * No library. Zoom is a CSS transform on one image whose position is clamped
 * so the photograph can never be dragged off screen, and the large files are
 * only requested when the viewer opens. Markup lives in
 * snippets/korsha-product-media.liquid.
 */

const DESKTOP = window.matchMedia('(min-width: 990px)');
const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');
const HOVER_ZOOM_DELAY = 180;
const DOUBLE_TAP_MS = 300;
const SWIPE_DISTANCE = 50;

/** @param {number} value @param {number} min @param {number} max */
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/* -------------------------------------------------------------------------
   Gallery
   ---------------------------------------------------------------------- */

class KorshaGallery extends HTMLElement {
  #bound = false;
  #current = 0;
  #raf = 0;
  /** @type {HTMLElement | null} */
  #zoomFrame = null;
  /** @type {number | undefined} */
  #zoomTimer;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    this.track = /** @type {HTMLElement | null} */ (this.querySelector('[data-korsha-gallery-track]'));
    if (!this.track) return;

    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.track.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('click', this.#onClick);

    if (this.hasAttribute('data-hover-zoom')) {
      this.addEventListener('pointermove', this.#onPointerMove);
      this.addEventListener('pointerleave', this.#endZoom);
    }

    this.closest('[data-korsha-product]')?.addEventListener('korsha:variant-change', (event) => {
      this.#onVariantChange(/** @type {CustomEvent} */ (event).detail?.variant);
    });

    DESKTOP.addEventListener('change', () => this.#setCurrent(this.#indexFromScroll()));
    this.#setCurrent(0);
  }

  /** Visible media items, in page order. */
  get items() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.track?.querySelectorAll('[data-korsha-gallery-item]:not([hidden])') || [])
    );
  }

  /** True when the gallery is currently one-image-at-a-time. */
  get isSwipe() {
    return DESKTOP.matches
      ? this.classList.contains('korsha-gallery--carousel')
      : this.classList.contains('korsha-gallery--mobile-carousel');
  }

  /**
   * @param {number} index
   * @param {boolean} [smooth]
   */
  goTo(index, smooth = true) {
    const items = this.items;
    const item = items[clamp(index, 0, items.length - 1)];
    if (!item || !this.track) return;
    this.track.scrollTo({ left: item.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    this.#setCurrent(items.indexOf(item));
  }

  /** @param {string} mediaId */
  goToMedia(mediaId, smooth = true) {
    const index = this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
    if (index > -1 && this.isSwipe) this.goTo(index, smooth);
  }

  #onScroll = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      if (this.isSwipe) this.#setCurrent(this.#indexFromScroll());
    });
  };

  #indexFromScroll() {
    if (!this.track || !this.isSwipe) return this.#current;
    const width = this.track.clientWidth || 1;
    return Math.round(Math.abs(this.track.scrollLeft) / width);
  }

  /** @param {number} index */
  #setCurrent(index) {
    const items = this.items;
    if (!items.length) return;
    this.#current = clamp(index, 0, items.length - 1);
    const active = items[this.#current];
    const activeId = active?.dataset.mediaId;

    const current = this.querySelector('[data-korsha-gallery-current]');
    const total = this.querySelector('[data-korsha-gallery-total]');
    if (current) current.textContent = String(this.#current + 1);
    if (total) total.textContent = String(items.length);

    this.querySelectorAll('[data-korsha-gallery-dot]').forEach((dot) => {
      dot.toggleAttribute('data-active', dot.getAttribute('data-media-id') === activeId);
    });

    this.querySelectorAll('[data-korsha-thumb]').forEach((thumb) => {
      if (thumb.getAttribute('data-media-id') === activeId) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    const [previous, next] = this.querySelectorAll('[data-korsha-gallery-step]');
    if (previous instanceof HTMLButtonElement) previous.disabled = this.#current === 0;
    if (next instanceof HTMLButtonElement) next.disabled = this.#current === items.length - 1;

    // A video that has scrolled out of view should not keep playing.
    if (this.isSwipe) {
      items.forEach((item, itemIndex) => {
        if (itemIndex !== this.#current) item.querySelector('video')?.pause();
      });
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.isSwipe || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
    event.preventDefault();
    this.goTo(this.#current + (event.key === 'ArrowRight' ? 1 : -1));
    this.items[this.#current]?.querySelector('button')?.focus({ preventScroll: true });
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const step = target.closest('[data-korsha-gallery-step]');
    if (step) {
      const hadFocus = document.activeElement === step;
      this.goTo(this.#current + Number(step.getAttribute('data-korsha-gallery-step')));
      // Reaching either end disables that arrow; keep keyboard focus on the
      // arrow that can still move rather than letting it fall to the page.
      if (step instanceof HTMLButtonElement && step.disabled && hadFocus) {
        const other = Array.from(this.querySelectorAll('[data-korsha-gallery-step]')).find((button) => button !== step);
        if (other instanceof HTMLButtonElement) other.focus({ preventScroll: true });
      }
      return;
    }

    const thumb = target.closest('[data-korsha-thumb]');
    if (thumb) {
      const index = this.items.findIndex((item) => item.dataset.mediaId === thumb.getAttribute('data-media-id'));
      if (index > -1) this.goTo(index);
      return;
    }

    const play = target.closest('[data-korsha-deferred-play]');
    if (play) {
      const container = play.closest('[data-korsha-deferred]');
      if (container instanceof HTMLElement) this.#loadDeferred(container);
      return;
    }

    const opener = target.closest('[data-korsha-lightbox-open]');
    if (opener) {
      const dialog = document.getElementById(this.getAttribute('data-lightbox') || '');
      if (!(dialog instanceof HTMLDialogElement)) return;
      this.#endZoom();
      const mediaId = opener.closest('[data-korsha-gallery-item]')?.getAttribute('data-media-id') || '';
      Lightbox.for(dialog, this).open(mediaId);
    }
  };

  /** @param {HTMLElement} container */
  #loadDeferred(container) {
    if (container.dataset.loaded) return;
    const template = container.querySelector('template');
    if (!template) return;

    container.dataset.loaded = 'true';
    container.querySelector('[data-korsha-deferred-play]')?.remove();
    container.appendChild(template.content.cloneNode(true));

    const media = container.querySelector('iframe, video, model-viewer');
    if (media instanceof HTMLElement) media.focus?.();

    if (container.hasAttribute('data-korsha-model')) {
      const model = container.querySelector('model-viewer');
      const shopify = /** @type {any} */ (window).Shopify;
      shopify?.loadFeatures?.([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (/** @type {unknown} */ errors) => {
            if (errors || !model || !shopify.ModelViewerUI) return;
            new shopify.ModelViewerUI(model);
          },
        },
      ]);
    }
  }

  /* --- Hover magnifier --------------------------------------------------- */

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerType !== 'mouse' || !FINE_POINTER.matches || !DESKTOP.matches) return;

    const target = event.target instanceof Element ? event.target : null;
    const frame = /** @type {HTMLElement | null} */ (target?.closest('.korsha-gallery__item--image .korsha-gallery__frame') || null);

    if (!frame) {
      this.#endZoom();
      return;
    }

    const rect = frame.getBoundingClientRect();
    frame.style.setProperty('--korsha-zoom-x', `${clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100)}%`);
    frame.style.setProperty('--korsha-zoom-y', `${clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100)}%`);

    if (this.#zoomFrame === frame) return;
    this.#endZoom();
    this.#zoomFrame = frame;
    // A short rest before magnifying, so moving the pointer across the page
    // does not flash every image it passes.
    this.#zoomTimer = window.setTimeout(() => this.#startZoom(frame), HOVER_ZOOM_DELAY);
  };

  /** @param {HTMLElement} frame */
  #startZoom(frame) {
    if (this.#zoomFrame !== frame) return;
    const image = frame.querySelector('.korsha-gallery__image');
    const source = image?.getAttribute('data-zoom-src');
    if (!source) return;

    let zoom = /** @type {HTMLImageElement | null} */ (frame.querySelector('.korsha-gallery__zoom'));
    if (!zoom) {
      zoom = new Image();
      zoom.className = 'korsha-gallery__zoom';
      zoom.alt = '';
      zoom.decoding = 'async';
      zoom.setAttribute('aria-hidden', 'true');
      const ready = () => zoom?.classList.add('is-ready');
      zoom.addEventListener('load', ready, { once: true });
      zoom.src = source;
      frame.appendChild(zoom);
      if (zoom.complete) ready();
    }
    frame.classList.add('is-zooming');
  }

  #endZoom = () => {
    window.clearTimeout(this.#zoomTimer);
    this.#zoomFrame?.classList.remove('is-zooming');
    this.#zoomFrame = null;
  };

  /* --- Variants ------------------------------------------------------------ */

  /** @param {{ featured_media?: { id: number } } | null | undefined} variant */
  #onVariantChange(variant) {
    const mediaId = variant?.featured_media?.id ? String(variant.featured_media.id) : '';
    if (!this.track) return;

    if (this.hasAttribute('data-hide-variant-media') && mediaId) {
      this.track.querySelectorAll('[data-korsha-gallery-item][data-variant-media]').forEach((item) => {
        const hide = item.getAttribute('data-media-id') !== mediaId;
        item.toggleAttribute('hidden', hide);
        this.querySelectorAll(`[data-media-id="${item.getAttribute('data-media-id')}"]:not([data-korsha-gallery-item])`).forEach(
          (companion) => companion.toggleAttribute('hidden', hide)
        );
      });
    }

    if (!mediaId) return;
    const item = this.track.querySelector(`[data-korsha-gallery-item][data-media-id="${mediaId}"]`);
    if (!(item instanceof HTMLElement)) return;

    if (this.isSwipe) {
      this.goTo(this.items.indexOf(item));
      return;
    }

    // Stacked layouts: the chosen colour's photograph moves to the top of the
    // stack. The page itself is never scrolled.
    if (this.track.firstElementChild !== item) {
      this.track.prepend(item);
      const thumb = this.querySelector(`[data-korsha-thumb][data-media-id="${mediaId}"]`);
      thumb?.parentElement?.prepend(thumb);
      const dot = this.querySelector(`[data-korsha-gallery-dot][data-media-id="${mediaId}"]`);
      dot?.parentElement?.prepend(dot);
    }
    this.#setCurrent(0);
  }
}

/* -------------------------------------------------------------------------
   Lightbox
   ---------------------------------------------------------------------- */

/** @type {WeakMap<HTMLDialogElement, Lightbox>} */
const lightboxes = new WeakMap();

class Lightbox {
  /**
   * @param {HTMLDialogElement} dialog
   * @param {KorshaGallery} gallery
   */
  static for(dialog, gallery) {
    let instance = lightboxes.get(dialog);
    if (!instance) {
      instance = new Lightbox(dialog, gallery);
      lightboxes.set(dialog, instance);
    }
    instance.gallery = gallery;
    return instance;
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {KorshaGallery} gallery
   */
  constructor(dialog, gallery) {
    this.dialog = dialog;
    this.gallery = gallery;
    this.stage = /** @type {HTMLElement} */ (dialog.querySelector('[data-korsha-lightbox-stage]'));
    this.slides = /** @type {HTMLElement[]} */ (Array.from(dialog.querySelectorAll('[data-korsha-lightbox-slide]')));
    this.counter = dialog.querySelector('[data-korsha-lightbox-current]');
    this.zoomButton = dialog.querySelector('[data-korsha-lightbox-zoom]');

    this.index = 0;
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.maxScale = 3;
    this.base = { width: 0, height: 0 };

    /** @type {Map<number, { x: number, y: number }>} */
    this.pointers = new Map();
    /** @type {{ x: number, y: number, tx: number, ty: number, moved: boolean } | null} */
    this.gesture = null;
    /** @type {{ distance: number, scale: number, midX: number, midY: number, x: number, y: number } | null} */
    this.pinch = null;
    /** @type {{ time: number, x: number, y: number } | null} */
    this.lastTap = null;

    dialog.addEventListener('click', this.#onClick);
    dialog.addEventListener('keydown', this.#onKeydown);
    dialog.addEventListener('close', this.#onClose);
    this.stage.addEventListener('pointerdown', this.#onPointerDown);
    this.stage.addEventListener('pointermove', this.#onPointerMove);
    this.stage.addEventListener('pointerup', this.#onPointerUp);
    this.stage.addEventListener('pointercancel', this.#onPointerUp);
    this.stage.addEventListener('dragstart', (event) => event.preventDefault());
    window.addEventListener('resize', () => this.dialog.open && this.layout());
  }

  get image() {
    return /** @type {HTMLImageElement | null} */ (this.slides[this.index]?.querySelector('img') || null);
  }

  /** @param {string} mediaId */
  open(mediaId) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    this.show(index > -1 ? index : 0, false);

    if (!this.dialog.open) {
      this.dialog.showModal();
      if (window.KorshaDrawers) window.KorshaDrawers.lockScroll();
      else document.documentElement.setAttribute('scroll-lock', '');
    }
    this.layout();
    /** @type {HTMLElement | null} */ (this.dialog.querySelector('[data-korsha-lightbox-close]'))?.focus();
  }

  /**
   * @param {number} index
   * @param {boolean} [animate]
   */
  show(index, animate = true) {
    this.index = clamp(index, 0, this.slides.length - 1);

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;
      slide.hidden = !active;
      slide.classList.toggle('is-entering', active && animate);
    });

    [this.index, this.index + 1, this.index - 1].forEach((slideIndex) => this.#loadSlide(slideIndex));
    if (this.counter) this.counter.textContent = String(this.index + 1);

    this.scale = 1;
    this.layout();
  }

  /** @param {number} index */
  #loadSlide(index) {
    const image = this.slides[index]?.querySelector('img');
    if (!image || image.getAttribute('src')) return;
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.src) image.src = image.dataset.src;
  }

  /** Fits the current image to the stage, then re-applies any zoom. */
  layout() {
    const image = this.image;
    if (!image) return;
    const stageWidth = this.stage.clientWidth;
    const stageHeight = this.stage.clientHeight;
    const naturalWidth = Number(image.getAttribute('width')) || image.naturalWidth || stageWidth;
    const naturalHeight = Number(image.getAttribute('height')) || image.naturalHeight || stageHeight;

    const fit = Math.min(stageWidth / naturalWidth, stageHeight / naturalHeight);
    this.base = { width: naturalWidth * fit, height: naturalHeight * fit };
    image.style.width = `${this.base.width}px`;
    image.style.height = `${this.base.height}px`;

    // Up to the photograph's own resolution, never less than 2.5x so fabric
    // detail is reachable even on a large screen, never more than 4x.
    this.maxScale = clamp(naturalWidth / this.base.width, 2.5, 4);

    if (this.scale <= 1) {
      this.scale = 1;
      this.x = (stageWidth - this.base.width) / 2;
      this.y = (stageHeight - this.base.height) / 2;
    }
    this.apply(false);
  }

  /** @param {boolean} animate */
  apply(animate) {
    const image = this.image;
    if (!image) return;
    const stageWidth = this.stage.clientWidth;
    const stageHeight = this.stage.clientHeight;
    const width = this.base.width * this.scale;
    const height = this.base.height * this.scale;

    this.x = width <= stageWidth ? (stageWidth - width) / 2 : clamp(this.x, stageWidth - width, 0);
    this.y = height <= stageHeight ? (stageHeight - height) / 2 : clamp(this.y, stageHeight - height, 0);

    this.stage.classList.toggle('is-animating', animate);
    image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;

    const zoomed = this.scale > 1.01;
    this.stage.classList.toggle('is-zoomed', zoomed);
    this.zoomButton?.setAttribute('aria-pressed', String(zoomed));
  }

  /**
   * Zooms to `scale`, keeping the image point under (px, py) where it is.
   * @param {number} scale
   * @param {number} px
   * @param {number} py
   * @param {boolean} animate
   */
  zoomTo(scale, px, py, animate) {
    const imageX = (px - this.x) / this.scale;
    const imageY = (py - this.y) / this.scale;
    this.scale = clamp(scale, 1, this.maxScale);
    this.x = px - imageX * this.scale;
    this.y = py - imageY * this.scale;
    this.apply(animate);
  }

  /** @param {boolean} animate */
  reset(animate) {
    this.scale = 1;
    this.apply(animate);
  }

  /** @param {number} direction */
  step(direction) {
    const next = this.index + direction;
    if (next < 0 || next >= this.slides.length) {
      this.reset(true);
      return;
    }
    this.show(next);
  }

  /** @param {PointerEvent} event */
  #stagePoint(event) {
    const rect = this.stage.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  /** On desktop a zoomed image follows the pointer across the frame. @param {PointerEvent} event */
  #follow(event) {
    const point = this.#stagePoint(event);
    const stageWidth = this.stage.clientWidth;
    const stageHeight = this.stage.clientHeight;
    const width = this.base.width * this.scale;
    const height = this.base.height * this.scale;
    this.x = -(width - stageWidth) * (point.x / stageWidth);
    this.y = -(height - stageHeight) * (point.y / stageHeight);
    this.apply(false);
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    this.stage.setPointerCapture?.(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.pointers.size === 1) {
      this.gesture = { x: event.clientX, y: event.clientY, tx: this.x, ty: this.y, moved: false };
    } else if (this.pointers.size === 2) {
      const [a, b] = Array.from(this.pointers.values());
      const rect = this.stage.getBoundingClientRect();
      this.pinch = {
        distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale: this.scale,
        midX: (a.x + b.x) / 2 - rect.left,
        midY: (a.y + b.y) / 2 - rect.top,
        x: this.x,
        y: this.y,
      };
      if (this.gesture) this.gesture.moved = true;
    }
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.pointers.has(event.pointerId)) {
      if (event.pointerType === 'mouse' && this.scale > 1.01) this.#follow(event);
      return;
    }
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.pointers.size >= 2 && this.pinch) {
      const [a, b] = Array.from(this.pointers.values());
      const rect = this.stage.getBoundingClientRect();
      const midX = (a.x + b.x) / 2 - rect.left;
      const midY = (a.y + b.y) / 2 - rect.top;
      const scale = clamp((this.pinch.scale * Math.hypot(a.x - b.x, a.y - b.y)) / this.pinch.distance, 1, this.maxScale);
      const imageX = (this.pinch.midX - this.pinch.x) / this.pinch.scale;
      const imageY = (this.pinch.midY - this.pinch.y) / this.pinch.scale;
      this.scale = scale;
      this.x = midX - imageX * scale;
      this.y = midY - imageY * scale;
      this.apply(false);
      return;
    }

    const gesture = this.gesture;
    if (!gesture) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (Math.hypot(dx, dy) > 6) gesture.moved = true;

    if (event.pointerType === 'mouse') {
      if (this.scale > 1.01) this.#follow(event);
      return;
    }

    if (this.scale > 1.01) {
      this.x = gesture.tx + dx;
      this.y = gesture.ty + dy;
      this.apply(false);
    } else if (this.image) {
      // Unzoomed: the photograph follows the finger as feedback for a swipe.
      this.image.style.transform = `translate3d(${gesture.tx + dx}px, ${gesture.ty + Math.max(0, dy) * 0.5}px, 0) scale(1)`;
    }
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    this.pointers.delete(event.pointerId);

    if (this.pinch) {
      if (this.pointers.size < 2) {
        this.pinch = null;
        if (this.scale < 1.05) this.reset(true);
        else this.apply(true);
        const [remaining] = Array.from(this.pointers.values());
        this.gesture = remaining ? { x: remaining.x, y: remaining.y, tx: this.x, ty: this.y, moved: true } : null;
      }
      return;
    }

    const gesture = this.gesture;
    if (!gesture || this.pointers.size) return;
    this.gesture = null;

    if (event.type === 'pointercancel') {
      this.apply(true);
      return;
    }

    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    const point = this.#stagePoint(event);

    if (gesture.moved) {
      if (event.pointerType !== 'mouse' && this.scale <= 1.01) {
        if (Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
          this.step(dx < 0 ? 1 : -1);
        } else if (dy > 90 && dy > Math.abs(dx)) {
          this.dialog.close();
        } else {
          this.reset(true);
        }
      } else if (event.pointerType !== 'mouse') {
        this.apply(true);
      }
      return;
    }

    if (event.pointerType === 'mouse') {
      if (this.scale > 1.01) {
        this.reset(true);
      } else {
        this.zoomTo(this.maxScale, point.x, point.y, true);
      }
      return;
    }

    const now = Date.now();
    const last = this.lastTap;
    if (last && now - last.time < DOUBLE_TAP_MS && Math.hypot(point.x - last.x, point.y - last.y) < 30) {
      this.lastTap = null;
      if (this.scale > 1.01) this.reset(true);
      else this.zoomTo(this.maxScale, point.x, point.y, true);
    } else {
      this.lastTap = { time: now, x: point.x, y: point.y };
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-korsha-lightbox-close]')) {
      this.dialog.close();
      return;
    }

    const step = target.closest('[data-korsha-lightbox-step]');
    if (step) {
      this.step(Number(step.getAttribute('data-korsha-lightbox-step')));
      return;
    }

    if (target.closest('[data-korsha-lightbox-zoom]')) {
      if (this.scale > 1.01) this.reset(true);
      else this.zoomTo(this.maxScale, this.stage.clientWidth / 2, this.stage.clientHeight / 2, true);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.step(1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.step(-1);
        break;
      case '+':
      case '=':
        this.zoomTo(this.scale + 0.75, this.stage.clientWidth / 2, this.stage.clientHeight / 2, true);
        break;
      case '-':
        this.zoomTo(this.scale - 0.75, this.stage.clientWidth / 2, this.stage.clientHeight / 2, true);
        break;
    }
  };

  #onClose = () => {
    this.pointers.clear();
    this.pinch = null;
    this.gesture = null;
    this.reset(false);

    if (window.KorshaDrawers) window.KorshaDrawers.releaseScroll();
    else if (!document.querySelector('dialog.korsha-drawer[open], dialog.korsha-lightbox[open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    // Leave the gallery on the photograph the shopper was last looking at.
    const mediaId = this.slides[this.index]?.dataset.mediaId;
    if (mediaId) this.gallery?.goToMedia(mediaId, false);
  };
}

if (!customElements.get('korsha-gallery')) {
  customElements.define('korsha-gallery', KorshaGallery);
}
